import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart as LineIcon, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { MetricChart } from '../traffic-manager/tmCharts';
import { useFmt } from '../traffic-manager/tmShared';
import type { DayPoint } from '../../services/trafficManagerService';

const CHAVE = 'campaigns.performance.open';
const lerAberto = () => { try { return localStorage.getItem(CHAVE) !== '0'; } catch { return true; } };

const soma = (dias: DayPoint[]) => dias.reduce((a, d) => ({ spend: a.spend + d.spend, clicks: a.clicks + d.clicks, conv: a.conv + d.conversions }), { spend: 0, clicks: 0, conv: 0 });

/**
 * Desempenho dia a dia das campanhas filtradas (os mesmos gráficos do gestor
 * de tráfego): gasto, conversões, custo por conversão e CPC, dias fechados.
 */
const CampaignsPerformance: React.FC<{ diaria: DayPoint[] | null; loading: boolean; currency: string; count: number }> = ({ diaria, loading, currency, count }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const [aberto, setAberto] = useState(lerAberto);
  const alternar = () => setAberto((v) => { try { localStorage.setItem(CHAVE, v ? '0' : '1'); } catch { /* sem storage */ } return !v; });
  const dias = diaria || [];
  const s7 = soma(dias.slice(-7));
  const lbl = t('trafficManager.overview.last7', 'últimos 7 dias');
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40">
      <button type="button" onClick={alternar} aria-expanded={aberto} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <LineIcon className="w-5 h-5 text-primary-600" />
        <span className="font-semibold text-gray-900 dark:text-white">{t('campaigns.performance.title', 'Desempenho dia a dia')}</span>
        <span className="text-xs text-gray-500">{t('campaigns.performance.sub', '{{n}} campanha(s) do filtro · 30 dias fechados', { n: count })}</span>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        <span className="ml-auto text-gray-400">{aberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {aberto && (
        <div className="px-4 pb-4">
          {!dias.length
            ? <div className="h-36 flex items-center justify-center text-sm text-gray-500">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('campaigns.performance.empty', 'Sem dados diários para estas campanhas.')}</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                <MetricChart title={t('trafficManager.metrics.spend', 'Gasto')} data={dias} metric="spend" kind="bar" cur={currency} actions={[]} headline={f.money(s7.spend, currency)} headlineLabel={lbl} />
                <MetricChart title={t('trafficManager.metrics.conversions', 'Conversões')} data={dias} metric="conversions" kind="bar" cur={currency} actions={[]} headline={f.int(s7.conv)} headlineLabel={lbl} />
                <MetricChart title={t('trafficManager.metrics.cpa', 'Custo/conv.')} data={dias} metric="cpa" cur={currency} actions={[]} headline={s7.conv ? f.money(s7.spend / s7.conv, currency) : '—'} headlineLabel={lbl} />
                <MetricChart title={t('trafficManager.metrics.cpc', 'CPC')} data={dias} metric="cpc" cur={currency} actions={[]} headline={s7.clicks ? f.money(s7.spend / s7.clicks, currency) : '—'} headlineLabel={lbl} />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default CampaignsPerformance;
